import apiService from './apiService';

/**
 * Support-Tickets (Hilfe › Support-Tickets): kostenpflichtige Aufträge an unser
 * Team, wenn der KI-Agent etwas nicht so macht wie gewünscht.
 *
 * „ticket" allein meint im Dashboard schon das Testanruf-Ticket
 * (voiceService.createProbeTicket) — deshalb durchgehend „supportTicket".
 */

export type SupportTicketChannel = 'voice' | 'whatsapp';
export type SupportTicketStatus =
  | 'open'
  | 'in_progress'
  | 'waiting_for_customer'
  | 'resolved'
  | 'withdrawn';

export interface SupportTicketOption {
  code: string;
  label: string;
}

/** Feldgrenzen — kommen vom Backend, damit beide Seiten dieselben Zahlen prüfen. */
export interface SupportTicketLimits {
  description_min: number;
  description_max: number;
  expected_min: number;
  expected_max: number;
  message_max: number;
  contact_name_max: number;
  contact_phone_max: number;
  contact_email_max: number;
}

export interface SupportTicketConfig {
  price_eur: number;
  included_hours: number;
  categories: SupportTicketOption[];
  frequencies: SupportTicketOption[];
  limits: SupportTicketLimits;
}

export interface SupportTicketSummary {
  id: number;
  status: SupportTicketStatus;
  channel: SupportTicketChannel;
  agent_number: string | null;
  category: string;
  category_label: string;
  price_eur: number;
  billable: boolean | null;
  created_at: string | null;
  updated_at: string | null;
  resolved_at: string | null;
  withdrawn_at: string | null;
  last_message_at: string | null;
  last_message_author: 'customer' | 'team' | null;
  can_reply: boolean;
  can_withdraw: boolean;
}

export interface SupportTicketMessage {
  id: number;
  author_type: 'customer' | 'team';
  author_name: string | null;
  body: string;
  created_at: string | null;
}

export interface SupportTicketDetail extends SupportTicketSummary {
  conversation_id: number | null;
  conversation_started_at: string | null;
  occurred_at: string | null;
  description: string;
  expected_behavior: string;
  frequency: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string | null;
  messages: SupportTicketMessage[];
}

export interface SupportTicketCreate {
  channel: SupportTicketChannel;
  agent_number: string | null;
  category: string;
  conversation_id: number | null;
  /** ISO 8601 mit Zeitzone — Pflicht, wenn kein Gespräch gewählt ist. */
  occurred_at: string | null;
  description: string;
  expected_behavior: string;
  frequency: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string | null;
  /** Der angezeigte Preis — weicht er vom aktuellen ab, lehnt das Backend ab. */
  accepted_price_eur: number;
  price_accepted: true;
}

export const SUPPORT_TICKET_STATUS_LABELS: Record<SupportTicketStatus, string> = {
  open: 'Eingereicht',
  in_progress: 'In Bearbeitung',
  waiting_for_customer: 'Rückfrage an dich',
  resolved: 'Gelöst',
  withdrawn: 'Zurückgezogen',
};

export const SUPPORT_TICKET_CHANNEL_LABELS: Record<SupportTicketChannel, string> = {
  voice: 'Telefon-Agent',
  whatsapp: 'WhatsApp-Agent',
};

class SupportTicketService {
  getConfig(): Promise<SupportTicketConfig> {
    return apiService.get<SupportTicketConfig>('/support-tickets/config');
  }

  async list(): Promise<SupportTicketSummary[]> {
    const res = await apiService.get<{ items: SupportTicketSummary[] }>('/support-tickets');
    return res.items;
  }

  get(id: number): Promise<SupportTicketDetail> {
    return apiService.get<SupportTicketDetail>(`/support-tickets/${id}`);
  }

  create(payload: SupportTicketCreate): Promise<SupportTicketDetail> {
    return apiService.post<SupportTicketDetail>('/support-tickets', payload);
  }

  reply(id: number, body: string): Promise<SupportTicketDetail> {
    return apiService.post<SupportTicketDetail>(`/support-tickets/${id}/messages`, { body });
  }

  withdraw(id: number): Promise<SupportTicketDetail> {
    return apiService.post<SupportTicketDetail>(`/support-tickets/${id}/withdraw`, {});
  }
}

const supportTicketService = new SupportTicketService();
export default supportTicketService;
